import { computed, readonly, ref, useContext, useRoute } from "@nuxtjs/composition-api";
import { addItemCommand } from "~/modules/checkout/composables/useCart/commands/addItemCommand";
import { applyCouponCommand } from "~/modules/checkout/composables/useCart/commands/applyCouponCommand";
import { loadCartCommand } from "~/modules/checkout/composables/useCart/commands/loadCartCommand";
import { loadTotalQtyCommand } from "~/modules/checkout/composables/useCart/commands/loadTotalQtyCommand";
import { removeCouponCommand } from "~/modules/checkout/composables/useCart/commands/removeCouponCommand";
import { removeItemCommand } from "~/modules/checkout/composables/useCart/commands/removeItemCommand";
import { updateItemQtyCommand } from "~/modules/checkout/composables/useCart/commands/updateItemQtyCommand";
import { updateItemCommand } from "~/modules/checkout/composables/useCart/commands/updateItemCommand";
import { Logger } from "~/helpers/logger";
import { AppliedCoupon, Cart, CartItemInterface, ProductInterface, ProductStockStatus } from "~/modules/GraphQL/types";
import { useCartStore } from "~/modules/checkout/stores/cart";
import { useWishlist } from "~/modules/wishlist/composables/useWishlist";
import { Product } from "~/modules/catalog/product/types";
import { ComposableFunctionArgs } from "~/composables";
import { UseCartErrors, UseCartInterface } from "./useCart";
import { useUiNotification, useSearchProducts } from "~/composables";
import useProductStock from "~/modules/catalog/product/composables/useProductStock";
import getProductListBySkusGql from "~/modules/checkout/composables/useCart/queries/getProductListBySkus.gql";
import { GET_PRODUCT_SEARCH } from "~/modules/catalog/product/queries/getProduct.gql";
import { useVoucher } from "~/composables";
import { ParamListVoucher, ParamAppliedCoupon } from "~/composables/useVoucher/useVoucher";
import { useCustomerStore } from "~/modules/customer/stores/customer";
import { getApiAxios, postApiAxios } from "~/helpers";
import api from "~/config/extensionsApi";
import { setSizeSwatch } from "~/modules/catalog/category/helpers";

interface ProductsResponse {
  productSearch: {
    items: any[];
    [key: string]: any;
  };
}

/**
 * Allows loading and manipulating cart of the current user.
 *
 * See the {@link UseCartInterface} for a list of methods and values available in this composable.
 */
export function useCart<CART extends Cart, CART_ITEM extends CartItemInterface, PRODUCT extends ProductInterface>(): UseCartInterface<
  CART,
  CART_ITEM,
  PRODUCT
> {
  const loading = ref<boolean>(false);
  const loadingVoucherCart = ref<boolean>(false);
  const error = ref<UseCartErrors>({
    addItem: null,
    updateItem: null,
    removeItem: null,
    updateItemQty: null,
    load: null,
    clear: null,
    applyCoupon: null,
    removeCoupon: null,
    loadTotalQty: null,
    freeGift: null,
    addFreeGift: null,
  });
  const { app } = useContext();
  const context = app.$vsf;
  const route = useRoute();

  const cartStore = useCartStore();
  const cart = computed(() => cartStore.cart as CART);
  const appliedCoupons = computed(() => cartStore.appliedCoupons as AppliedCoupon[]);
  const { postMultiCoupon, listAppliedCoupon, postMultiCouponCustomer } = useVoucher();
  const apiState = context.$magento.config.state;
  const { loading: wishlistLoading, afterAddingWishlistItemToCart } = useWishlist();
  const { send: sendNotification } = useUiNotification();
  const customerStore = useCustomerStore();
  const freeGiftConfig = ref<any>(null);
  const { fetchProducts } = useSearchProducts();

  /**
   * Assign new cart object
   * @param newCart
   *
   * @return void
   */
  const setCart = async (newCart: CART): Promise<void> => {
    Logger.debug("useCart.setCart", newCart);
    if (!newCart) {
      cartStore.$patch((state) => {
        state.cart = newCart;
      });
      return;
    }
    const configuredCart = await configCart(newCart);
    cartStore.$patch((state) => {
      state.cart = configuredCart;
    });
  };

  /**
   * Check if product is in the cart
   * @param product
   *
   * @return boolean
   */
  // const isInCart = (product: PRODUCT): boolean => !!cart.value?.items?.find((cartItem) => cartItem?.product?.uid === product.uid);
  const isInCart = (product: PRODUCT): boolean => {
    if (!product || !product.uid) {
      return false;
    }

    return !!cart.value?.items?.find((cartItem) => {
      return cartItem?.product?.uid && cartItem.product.uid === product.uid;
    });
  };

  /**
   * Configure cart with additional data like stock and parent products
   * @param cartData - Cart data to configure
   * @returns Configured cart data
   */
  const configCart = async (cartData: Cart): Promise<Cart> => {
    if (!cartData) {
      return { items: [] } as Cart;
    }
    if (!cartData.items || cartData.items.length === 0) {
      return cartData;
    }

    try {
      const { getProductListStock } = useProductStock();

      const productSkus = cartData.items.map((item) => item.product.sku);
      const paramSkus = productSkus.map((sku) => encodeURIComponent(sku)).join(",");
      const dataInstock = await getProductListStock(paramSkus);

      let configuredCart = { ...cartData };

      if (dataInstock && dataInstock.length > 0) {
        configuredCart.items = configuredCart.items.map((item) => {
          const stockInfo = dataInstock.find((stock) => stock.sku === item.product.sku);
          if (stockInfo) {
            return {
              ...item,
              dataInStock: stockInfo,
            };
          }
          return item;
        });
      }

      const productsParentSkus = [
        ...new Set([
          ...productSkus,
          ...productSkus.map((sku) => sku.split("-")[0]),
          ...productSkus.map((sku) => {
            const parts = sku.split("-");
            return parts.length > 1 ? `${parts[0]}-${parts[1]}` : parts[0];
          }),
        ]),
      ];

      const params = {
        skus: productsParentSkus,
      };
      const { data } = await fetchProducts(params);
      const productsParent = data || null;

      if (productsParent && productsParent.length > 0) {
        configuredCart.items = configuredCart.items.map((item) => {
          const parentProduct = productsParent.find((parent) => {
            const parentChild = parent.configurable_children.find((child) => {
              if (child.sku === item.product.sku) {
                return true;
              }
              return false;
            });
            if (parentChild) {
              return true;
            }
            return false;
          });

          if (parentProduct) {
            return {
              ...item,
              productParent: parentProduct,
            };
          }
          return item;
        });
      }

      return configuredCart as CART;
    } catch (error) {
      Logger.error("Error configuring cart:", error);
      return cartData;
    }
  };

  const load = async (
    { customQuery = {}, customHeaders = {}, realCart = false } = { customQuery: { cart: "cart" }, customHeaders: {} }
  ): Promise<void> => {
    Logger.debug("useCart.load");

    try {
      loading.value = true;
      const loadedCart = await loadCartCommand.execute(context, { customQuery, customHeaders, realCart });
      const configuredCart = await configCart(loadedCart);

      cartStore.$patch((state) => {
        state.cart = configuredCart;
      });
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error("useCart/load", err);
    } finally {
      loading.value = false;
    }
  };

  const clear = async ({ customQuery, customHeaders } = { customQuery: { cart: "cart" }, customHeaders: {} }): Promise<void> => {
    Logger.debug("useCart.clear");

    try {
      loading.value = true;
      context.$magento.config.state.removeCartId();
      const loadedCart = await loadCartCommand.execute(context, { customQuery, customHeaders });

      cartStore.$patch((state) => {
        state.cart = loadedCart;
      });
    } catch (err) {
      error.value.clear = err;
      Logger.error("useCart/clear", err);
    } finally {
      loading.value = false;
    }
  };

  const loadTotalQty = async (params?: ComposableFunctionArgs<{}>): Promise<void> => {
    Logger.debug("useCart.loadTotalQty");

    try {
      loading.value = true;
      const totalQuantity = await loadTotalQtyCommand.execute(context, params);

      cartStore.$patch((state) => {
        state.cart.total_quantity = totalQuantity || 0;
      });
    } catch (err) {
      error.value.loadTotalQty = err;
      Logger.error("useCart/loadTotalQty", err);
    } finally {
      loading.value = false;
    }
  };

  const updateItem = async ({ skuNew, uidRemove, currentCart, quantity, customQuery, customHeaders }): Promise<void> => {
    try {
      loading.value = true;
      const updateCart = await updateItemCommand.execute(context, {
        currentCart: cart.value,
        skuNew,
        uidRemove,
        quantity,
        customQuery,
        customHeaders,
      });

      error.value.updateItem = null;

      const configuredCart = await configCart(updateCart);
      cartStore.$patch((state) => {
        state.cart = configuredCart;
      });
    } catch (err) {
      error.value.removeItem = err;
      sendNotification({
        id: Symbol("update_product_error"),
        message: "Đã xảy ra lỗi khi điều chỉnh sản phẩm trong giỏ hàng!",
        type: "warning",
        icon: "error",
        persist: false,
        title: "Đã xảy ra lỗi khi điều chỉnh sản phẩm trong giỏ hàng!",
      });
      Logger.error("useCart/updateItem", err);
    } finally {
      loading.value = false;
    }
  };

  const addItem = async ({ product, quantity, productConfiguration, customQuery, customHeaders }): Promise<void> => {
    Logger.debug("useCart.addItem", { product, quantity });

    try {
      loading.value = true;

      if (!apiState.getCartId()) {
        await load({ realCart: true });
      }

      const updatedCart = await addItemCommand.execute(context, {
        currentCart: cart.value,
        product,
        quantity,
        productConfiguration,
        customQuery,
        customHeaders,
      });

      const configuredCart = await configCart(updatedCart);

      error.value.addItem = null;
      cartStore.$patch((state) => {
        state.cart = configuredCart;
      });

      // Lấy thông tin sản phẩm để hiển thị thông báo
      const skuProduct = product?.sku;
      const variantProductDetail = product?.configurable_children?.find((variant) => variant?.sku === skuProduct);
      const urlImage = variantProductDetail?.thumbnail || variantProductDetail?.image || product?.thumbnail_url;

      // Đẩy query params vào URL dựa vào sku của sản phẩm
      let urlPath = product?.url_key || product?.url_path;
      let colorLabel = "";
      let sizeLabel = "";
      const configurableOpt = product?.configurable_options_map;
      if (variantProductDetail && configurableOpt) {
        const colorId = variantProductDetail?.color;
        const sizeId = variantProductDetail?.size;
        if (colorId) {
          const colorOption = configurableOpt.find((opt) => opt.attribute_code === "color");
          colorLabel = colorOption?.values.find((val) => val?.value_index?.toString() === colorId.toString())?.label || "";
        }
        if (sizeId) {
          const sizeOption = configurableOpt.find((opt) => opt.attribute_code === "size");
          sizeLabel = sizeOption?.values.find((val) => val?.value_index?.toString() === sizeId.toString())?.label || "";
        }
      }
      if (colorLabel || sizeLabel) {
        const queryParams = [];
        if (colorLabel) {
          queryParams.push(`color=${encodeURIComponent(colorLabel)}`);
        }
        if (sizeLabel) {
          queryParams.push(`size=${encodeURIComponent(sizeLabel)}`);
        }
        if (queryParams.length > 0) {
          urlPath = `${urlPath}?${queryParams.join("&")}`;
        }
      }

      const productName = variantProductDetail?.name || product?.name;
      sendNotification({
        id: Symbol("add_product_success"),
        message: "Đã thêm sản phẩm vào giỏ hàng!",
        type: "secondary",
        icon: "",
        isProduct: true,
        urlImage: setSizeSwatch(urlImage),
        urlPath: urlPath,
        productName: productName,
        persist: false,
      });
    } catch (err) {
      error.value.addItem = err;
      Logger.error("useCart/addItem", err);
      sendNotification({
        id: Symbol("add_product_error"),
        message: "Đã xảy ra lỗi khi thêm sản phẩm vào giỏ hàng!",
        type: "warning",
        icon: "error",
        persist: false,
        title: "Đã xảy ra lỗi khi thêm sản phẩm vào giỏ hàng!",
      });
    } finally {
      if (!wishlistLoading.value && route.value.query?.wishlist) {
        afterAddingWishlistItemToCart({
          product,
          cartError: error.value.addItem,
        });
      }

      loading.value = false;
    }
  };

  const removeItem = async ({ product, customQuery, customHeaders }) => {
    Logger.debug("useCart.removeItem", { product });

    try {
      loading.value = true;
      const updatedCart = await removeItemCommand.execute(context, {
        currentCart: cart.value,
        product,
        customQuery,
        customHeaders,
      });

      error.value.removeItem = null;
      const configuredCart = await configCart(updatedCart);
      cartStore.$patch((state) => {
        state.cart = configuredCart;
      });
    } catch (err) {
      error.value.removeItem = err;
      Logger.error("useCart/removeItem", err);
    } finally {
      loading.value = false;
    }
  };

  const updateItemQty = async ({ product, quantity, customQuery = { updateCartItems: "updateCartItems" } }): Promise<void> => {
    Logger.debug("useCart.updateItemQty", {
      product,
      quantity,
    });

    if (quantity && quantity > 0) {
      try {
        loading.value = true;
        const updatedCart = await updateItemQtyCommand.execute(context, {
          currentCart: cart.value,
          product,
          quantity,
          customQuery,
        });

        const configuredCart = await configCart(updatedCart);

        error.value.updateItemQty = null;
        cartStore.$patch((state) => {
          state.cart = configuredCart;
        });
      } catch (err) {
        error.value.updateItemQty = err;
        Logger.error("useCart/updateItemQty", err);
      } finally {
        loading.value = false;
      }
    }
  };

  const handleCoupon = async (couponCode = null, customQuery = null): Promise<void> => {
    const variables = {
      currentCart: cart.value,
      customQuery,
      couponCode,
    };

    const { updatedCart, errors } = couponCode
      ? await applyCouponCommand.execute(context, variables)
      : await removeCouponCommand.execute(context, variables);

    if (errors) {
      throw errors[0];
    }

    if (updatedCart) {
      const configuredCart = await configCart(updatedCart);
      cartStore.$patch((state) => {
        state.cart = configuredCart;
      });
    }
  };

  const applyCoupon = async ({ couponCode, customQuery }): Promise<void> => {
    Logger.debug("useCart.applyCoupon");

    try {
      loading.value = true;
      await handleCoupon(couponCode, customQuery);
      error.value.applyCoupon = null;
    } catch (err) {
      error.value.applyCoupon = err;
      Logger.error("useCart/applyCoupon", err);
    } finally {
      loading.value = false;
    }
  };

  const applyMultiCoupon = async (couponCodes: string): Promise<void> => {
    Logger.debug("useCart.applyMultiCoupon");
    try {
      loading.value = true;
      loadingVoucherCart.value = true;
      const user = customerStore.user;
      const extensionAttributes = cart.value?.extension_attributes;
      const appliedCoupons = (extensionAttributes?.coupon_code_odoo?.split(",") || []).filter(code => code.trim() !== "");

      const couponIndex = appliedCoupons.indexOf(couponCodes);
      if (couponIndex === -1) {
        appliedCoupons.push(couponCodes);
      } else {
        appliedCoupons.splice(couponIndex, 1);
      }

      if (!cart.value?.id) {
        throw new Error("Cart ID is missing");
      }

      const param: ParamAppliedCoupon = {
        cartId: cart.value.id,
        couponCodes: appliedCoupons,
      };

      if (user) {
        await postMultiCouponCustomer(param);
      } else {
        await postMultiCoupon(param);
      }

      error.value.applyCoupon = null;
      await load();

      cartStore.$patch((state) => {
        state.appliedCoupons = listAppliedCoupon.value;
      });
    } catch (err) {
      error.value.applyCoupon = err;
      Logger.error("useCart/applyMultiCoupon", err);
    } finally {
      loading.value = false;
      loadingVoucherCart.value = false;
    }
  };

  const removeCoupon = async ({ customQuery }): Promise<void> => {
    Logger.debug("useCart.removeCoupon");

    try {
      loading.value = true;
      await handleCoupon(null, customQuery);
      error.value.applyCoupon = null;
    } catch (err) {
      error.value.removeCoupon = err;
      Logger.error("useCart/removeCoupon", err);
    } finally {
      loading.value = false;
    }
  };

  const canAddToCart = (product: Product, qty = 1) => {
    // eslint-disable-next-line no-underscore-dangle
    if (product?.__typename === "ConfigurableProduct") {
      return !!product?.configurable_product_options_selection?.variant?.uid;
    }
    // const inStock = product?.stock_status === ProductStockStatus.InStock;
    // const stockLeft = product?.only_x_left_in_stock === null
    //   ? true
    //   : qty <= product?.only_x_left_in_stock;
    return true;
  };

  const loadFreeGiftList = async (ruleId: string) => {
    try {
      loading.value = true;
      const cartId = cart.value.id;
      const response = await getApiAxios(api.freeGift.free_gift_list + `?storeCode=vi&cartId=${cartId}&rule_id=${ruleId}`);
      if (response && response.code === 200 && response.result) {
        freeGiftConfig.value = response.result;
      }
      error.value.freeGift = null;
    } catch (err) {
      error.value.freeGift = err;
      Logger.error("useCart/freeGift", err);
    } finally {
      loading.value = false;
    }
  };

  const addCartFreeGift = async (param: any) => {
    try {
      loading.value = true;
      param.gift_data.quote_id = cart.value.id;
      await postApiAxios(api.freeGift.free_gift_addcart + `?storeCode=vi`, param);
      error.value.addFreeGift = null;
    } catch (err) {
      error.value.addFreeGift = err;
      Logger.error("useCart/addFreeGift", err);
    } finally {
      loading.value = false;
    }
  };

  return {
    setCart,
    cart,
    appliedCoupons,
    loadTotalQty,
    isInCart,
    addItem,
    updateItem,
    load,
    removeItem,
    clear,
    updateItemQty,
    applyCoupon,
    removeCoupon,
    applyMultiCoupon,
    listAppliedCoupon,
    canAddToCart,
    loadFreeGiftList,
    addCartFreeGift,
    freeGiftConfig,
    loading: readonly(loading),
    error: readonly(error),
    loadingVoucherCart: readonly(loadingVoucherCart),
  };
}

export default useCart;
export * from "./useCart";
